import { Recognition } from "@shared/models";
import { getFullName } from "@shared/utils";

/**
 * The one name a "recent recognitions" row needs, resolved OUTSIDE the presentational row
 * (overview-rebuild-pr4-plan.md §4 "The person's name — resolved OUTSIDE the row"). `Recognition`
 * carries two differently-shaped name sources (`Recognition.tsx:12-20`): `user` (the RECIPIENT, an
 * object with `first_name`/`last_name`) and `created_by` (the GIVER, a denormalised full-name string
 * stamped at creation time by `api/app/services/recognition.service.ts`'s `getFullName(user)` call —
 * an audit column, not a live relation, so a later rename doesn't retroactively change it). Keeping
 * both sources, and the branch between them, out of `RecognitionListItem` removes the `as User` cast
 * that branch used to require there and lets the row interpolate one already-resolved string.
 *
 * Called once per row by `RecognitionList` (`RecognitionList.tsx:141,176`), which already knows which
 * tab it is rendering, so `isSent` is passed in rather than re-derived here.
 *
 * @param recognition - the row's recognition.
 * @param isSent - true on the Sent tab (name the RECIPIENT via `user`), false on Received (name the
 * GIVER via `created_by`).
 * @returns the resolved name, or exactly `null` when there is nothing to show. `created_by` is
 * optional (`Recognition.tsx:16`); a bare `getFullName` on a missing recipient also degrades to `""`,
 * which is normalised to `null` here rather than left as an empty string — the row's "no dash" rule
 * depends on this being `null`, never `""`/`undefined`.
 */
export const recognitionPersonName = (recognition: Recognition, isSent: boolean): string | null => {
  if (isSent) {
    return getFullName(recognition.user ?? null) || null;
  }
  // `||`, not `??`: `created_by` is optional AND a string, so `??` would let an empty string through
  // and this function's own contract above promises `null` rather than `""`. The row's "no dash" rule
  // is falsy-based today, so `""` would happen to render correctly — which is exactly why the wrong
  // operator here would survive unnoticed until something read the return value strictly.
  return recognition.created_by || null;
};
